import '@audio-network/layouts/main.css'
import '@audio-network/componentlibrary/main.css'

import * as gtm from '../lib/gtm'

import { FC, useEffect } from 'react'
import { appWithTranslation, i18n } from 'next-i18next'

import { AnwThemeProvider } from '@audio-network/componentlibrary'
import App from 'next/app'
import { AppProps } from 'next/app'
import { ClientToastContainer } from '@/components/shared/Toast/ClientToastContainer'
import CookieControl from '../components/app/CookieControl/CookieControl'
import GlobalStyle from '../styles/app'
import Head from 'next/head'
import Main from '@/components/layout/Main/Main'
import MusicAppLayoutWrapper from '../components/layout/MusicAppLayoutWrapper/MusicAppLayoutWrapper'
import { NewRelicAgent } from '../components/app/NewRelic/NewRelicAgent'
import { PlayerProvider } from '@audio-network/player'
import PrimaryLayout from '@/components/layout/PrimaryLayout/PrimaryLayout'
import { SessionProvider } from '../context/Session'
import { StyleSheetManager } from 'styled-components'
import getLocaleFromCookie from '@/utils/cookies/getLocaleFromCookie'
import { getToggleFromCookie } from '@/utils/cookies/getToggleFromCookie'
import styles from '@/styles/app.module.css'
import timeAgoLocales from '@/utils/formatting/timeAgoLocales'

interface MusicApp extends AppProps {
	showCookieControl: boolean
	showTrialBanner: boolean
	newSidebar: boolean
}

const isCookieControlLocale = (locale: string): boolean => {
	const showCookieControl =
		locale === 'en-US' ||
		locale === 'en-GB' ||
		locale === 'de-DE' ||
		locale === 'es-ES' ||
		locale === 'fr-FR' ||
		locale === 'it-IT' ||
		locale === 'pt-PT'

	return showCookieControl
}

const MusicApp = ({
	Component,
	pageProps,
	showCookieControl,
	showTrialBanner,
	newSidebar,
	router,
}: MusicApp): JSX.Element => {
	const navigationTranslations = i18n?.getResourceBundle(
		i18n.resolvedLanguage,
		'navigation'
	)

	// ON-52: Disable router prefetching to reduce contentful graphQL and image request Server Side for blurred images
	useEffect(() => {
		router.prefetch = () => Promise.resolve()
	}, [router])

	useEffect(() => {
		timeAgoLocales(getLocaleFromCookie())
	}, [])

	useEffect(() => {
		// the pageview event is already fired for the initial page load outside of the router events. Here we are just adding an event listener to fire a pageview event on route change
		const handleRouteChange = (url): void => {
			gtm.pageview(url)
		}
		router.events.on('routeChangeComplete', handleRouteChange)
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange)
		}
	}, [router.events])

	return (
		<>
			<Head>
				<meta name="viewport" content="width=device-width, minimum-scale=1.0" />
			</Head>
			<div className={styles.app}>
				<StyleSheetManager>
					<AnwThemeProvider>
						<GlobalStyle />
						<ClientToastContainer />
						<PlayerProvider>
							<SessionProvider>
								<PrimaryLayout
									translations={navigationTranslations}
									showTrialBanner={showTrialBanner}
									newSidebar={newSidebar}
								>
									<MusicAppLayoutWrapper
										interfaceText={navigationTranslations}
										newSidebar={newSidebar}
									>
										{/* Temporary location for MainStack */}
										<Main>
											<Component {...pageProps} />
										</Main>
									</MusicAppLayoutWrapper>
								</PrimaryLayout>
								{showCookieControl && <CookieControl />}
							</SessionProvider>
						</PlayerProvider>
					</AnwThemeProvider>
				</StyleSheetManager>
			</div>
			<NewRelicAgent />
		</>
	)
}

MusicApp.getInitialProps = async (context) => {
	const {
		ctx: { req, res },
	} = context

	const showCookieControl = isCookieControlLocale(context.router.locale)

	const appProps = await App.getInitialProps(context)

	const showTrialBanner = getToggleFromCookie('Banner', req, res)

	const newSidebar = getToggleFromCookie('NewSidebar', req, res)

	return {
		...appProps,
		showCookieControl,
		showTrialBanner,
		newSidebar,
	}
}

export default appWithTranslation(MusicApp as FC)
